import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import Button from '../../../Button';

import { Mic as MicIcon } from '@icons/dialer';

const { Sizes } = Button;

interface MicCheckButtonProps {
  className?: string;
  size?: (typeof Sizes)[keyof typeof Sizes];
}

const STATES = {
  DEFAULT: 'default',
  TIP: 'tip',
  CHECKING: 'checking',
  DETECTED: 'detected',
  NOT_DETECTED: 'not_detected',
};

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

export const MicCheckButton = ({
  className,
  size = Sizes.TINY,
}: LT.WithChildren<MicCheckButtonProps>): JSX.Element => {
  const [quality, setQuality] = useState(0);
  const [status, setStatus] = useState(STATES.DEFAULT);

  useEffect(() => {
    if (status !== STATES.CHECKING) return;

    let stream;
    let audioContext;

    const detectSound = async () => {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      const scriptProcessor = audioContext.createScriptProcessor();

      microphone.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination);

      let maxQuality = 0;

      scriptProcessor.onaudioprocess = () => {
        const array = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(array);
        const arraySum = array.reduce((a, value) => a + value, 0);
        const average = arraySum / array.length;
        maxQuality = Math.max(maxQuality, average);
        setQuality(average);
      };

      await wait(3000);
      setStatus(maxQuality > 0 ? STATES.DETECTED : STATES.NOT_DETECTED);

      scriptProcessor.disconnect();
      analyser.disconnect();
      microphone.disconnect();

      stream.getTracks().forEach(track => track.stop());
      stream = null;
      audioContext = null;

      await wait(1000);
      setStatus(STATES.DEFAULT);
    };

    detectSound();
  }, [status]);

  const handleClick = useCallback(async () => {
    setStatus(STATES.TIP);
    await wait(1000);
    setStatus(STATES.CHECKING);
  }, [setStatus]);

  return (
    <StyledMicCheckButton
      className={className}
      onClick={handleClick}
      size={size}
      offHover
    >
      <StyledIcon />
      {status === STATES.DEFAULT && 'Проверить'}
      {status === STATES.TIP && 'Говорите'}
      {status === STATES.CHECKING && (
        <StyledProgress>
          <StyledProgressBar style={{ width: `${quality}px` }} />
        </StyledProgress>
      )}
      {status === STATES.DETECTED && 'Все ок!'}
      {status === STATES.NOT_DETECTED && 'Не слышно!'}
    </StyledMicCheckButton>
  );
};

const StyledMicCheckButton = styled(Button)`
  display: flex;
  position: relative;
  gap: 7px;
  padding: 0 10px;
  align-items: center;
  min-width: 115px;
`;

const StyledIcon = styled(MicIcon)`
  width: 9px;
  height: 16px;

  > * {
    stroke: #007aff;
  }
`;

const StyledProgress = styled.div`
  height: 8px;
  border-radius: 6px;
  flex: 1;
  position: relative;
  overflow: hidden;
  border: 1px solid #007aff;
`;

const StyledProgressBar = styled.div`
  top: 0;
  left: 0;
  height: 100%;
  position: absolute;
  background: linear-gradient(to right, #a8ff78, #78ffd6);
`;
